// src/views/student-words-list/slice/actions.ts
import { AxiosError } from 'axios';
import { message } from 'antd';
import { isEqual } from 'lodash';
import slice from './slice';
import { ApiError, WordsListData } from '../../../services/ApiService.types';
import { AppThunk } from '../../../redux/store';
import API from '../../../services/ApiService';
import { setActiveList } from '../../auth/slice/actions';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import { Roles } from '../../../constants/Roles';
import i18n from '../../../i18n';

const {
  startLoadingList, setPageData, setLoadingListError, finishLoadingList,
  resetData,
} = slice.actions;

// Aktywna lista ucznia leży w przeglądarce razem ze słowami. Porównujemy ją z listami, które uczeń
// ma dziś udostępnione: zmienione słowa podmieniamy, a listę usuniętą, z cofniętym udostępnieniem
// albo skróconą poniżej 2 słów (ta sama granica co przy wyborze listy w ListCard i u nauczyciela) zdejmujemy z gier.
// Komunikat mówi raz, co się stało, żeby uczeń nie wziął zniknięcia gier za błąd aplikacji.
const reconcileActiveList = (records: WordsListData[]): AppThunk => (dispatch, getState) => {
  const state = getState();
  const activeList = getActiveList(state);

  if (!activeList || getUserData(state)?.role !== Roles.STUDENT) {
    return;
  }

  const freshList = records.find((list) => list.id === activeList.id);

  if (!freshList) {
    dispatch(setActiveList(null));
    message.info(i18n.t('words-list:studentLists.unavailable', { list: activeList.name }));
  } else if (freshList.words.length < 2) {
    dispatch(setActiveList(null));
    message.info(i18n.t('words-list:studentLists.tooFewWords', { list: freshList.name }));
  } else if (!isEqual(freshList, activeList)) {
    // Bez zmian nie podmieniamy — nowy obiekt ułożyłby od nowa planszę w Memory i Sparuj
    dispatch(setActiveList(freshList));
  }
};

const fetchStudentWordsListPage = (): AppThunk => (
  async (dispatch) => {
    dispatch(startLoadingList());
    try {
      const { data: { records } } = await API.students.wordsList();

      dispatch(setPageData({ records }));
      dispatch(reconcileActiveList(records));
    } catch (e) {
      const { response } = e as AxiosError<ApiError>;
      dispatch(setLoadingListError(response && response.data));
    } finally {
      dispatch(finishLoadingList());
    }
  });

// Przy starcie aplikacji. Uczeń, który po odświeżeniu strony wchodzi prosto do gry, nie mija ekranu
// swoich list, więc jego aktywną listę sprawdzamy osobno. Przy błędzie albo braku sieci zostaje, jak była.
const refreshActiveList = (): AppThunk => async (dispatch, getState) => {
  const state = getState();

  if (!getActiveList(state) || getUserData(state)?.role !== Roles.STUDENT) {
    return;
  }

  try {
    const { data: { records } } = await API.students.wordsList();

    dispatch(reconcileActiveList(records));
  } catch (e) {
    console.error('❌ Failed to refresh active list:', e);
  }
};

export {
  resetData,
  fetchStudentWordsListPage,
  refreshActiveList,
};
